import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AgentApiService } from '../../../../shared/services/agent-api/agent-api.service';
import { LoggerService } from '../../../../services/logger-services/logger.service';
import { applyTenantInsIdsToAnswer } from '../../../../shared/services/regulation-reference-mapping/apply-tenant-ins-ids-to-answer';
import { RegulationReferenceMappingService } from '../../../../shared/services/regulation-reference-mapping/regulation-reference-mapping.service';
import { TokenService } from '../../../../shared/services/token/token.service';
import {
  AgentSourceMetadata,
  SaveAgentResponseRequest,
  StreamCompletionMeta
} from '../../models/save-agent-response.interface';

const SAVE_AGENT_RESPONSE_ENDPOINT = 'save_agent_response';

function normalizeStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === 'string' ? item.trim() : ''))
      .filter((item) => item.length > 0);
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    return [value.trim()];
  }
  return [];
}

function parseResponseTime(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
}

function applyCentralizedResponseFields(
  payload: SaveAgentResponseRequest,
  meta: StreamCompletionMeta
): void {
  if (meta.message_id) {
    payload.message_id = meta.message_id;
  }
  if (meta.response_metadata) {
    payload.response_metadata = meta.response_metadata;
  }
  if (meta.feedback !== undefined) {
    payload.feedback = meta.feedback;
  }
}

/**
 * Extract supervisor, agents, response_time, and related fields from a stream completion event.
 */
export function extractStreamCompletionMeta(
  eventData: Record<string, unknown>,
  fallbackSupervisor?: string[],
  fallbackAgents?: string[]
): StreamCompletionMeta {
  const nested = asRecord(eventData['metadata']) || {};
  const sourceMetadata = asRecord(eventData['source_metadata'] ?? nested['source_metadata']);
  const responseMetadata = asRecord(eventData['response_metadata']);

  const supervisor = normalizeStringList(
    nested['supervisor'] ?? eventData['supervisor'] ?? fallbackSupervisor
  );
  const streamAgents = normalizeStringList(nested['agents'] ?? eventData['agents']);
  const agents = streamAgents.length > 0 ? streamAgents : normalizeStringList(fallbackAgents);
  const file_path = normalizeStringList(nested['file_path'] ?? eventData['file_path']);

  const response_id =
    typeof eventData['response_id'] === 'string' && eventData['response_id'].trim().length > 0
      ? eventData['response_id'].trim()
      : undefined;

  const message_id =
    typeof eventData['message_id'] === 'string' && eventData['message_id'].trim().length > 0
      ? eventData['message_id'].trim()
      : undefined;

  const updated_at =
    typeof eventData['updated_at'] === 'string' && eventData['updated_at'].trim().length > 0
      ? eventData['updated_at'].trim()
      : undefined;

  return {
    supervisor,
    agents,
    response_time: parseResponseTime(eventData['response_time']),
    response_id,
    message_id,
    updated_at,
    file_path: file_path.length > 0 ? file_path : undefined,
    metadata: Object.keys(nested).length > 0 ? nested : undefined,
    source_metadata: sourceMetadata as AgentSourceMetadata | undefined,
    response_metadata: responseMetadata,
    feedback: eventData['feedback']
  };
}

export function buildSaveAgentResponsePayload(params: {
  thread_id: string;
  tenant_id: string;
  user_id: string;
  question: string;
  answerText: string;
  thinking?: string;
  imageVisualization?: string;
  sourceMetadata?: AgentSourceMetadata;
  meta: StreamCompletionMeta;
}): SaveAgentResponseRequest | null {
  const thread_id = params.thread_id?.trim();
  const tenant_id = params.tenant_id?.trim();
  const user_id = params.user_id?.trim();
  const question = params.question?.trim();
  const answerText = params.answerText?.trim();

  if (!thread_id || !tenant_id || !user_id || !question || !answerText) {
    return null;
  }

  const answer: SaveAgentResponseRequest['answer'] = {
    text: answerText,
    is_complete: true,
    thread_id
  };

  const thinking = params.thinking?.trim();
  if (thinking) {
    answer.thinking = thinking;
  }

  if (params.meta.response_id) {
    answer.response_id = params.meta.response_id;
  }

  const metadata: NonNullable<SaveAgentResponseRequest['answer']['metadata']> = {
    ...params.meta.metadata
  };
  if (params.meta.supervisor.length > 0) {
    metadata.supervisor = params.meta.supervisor;
  }
  if (params.meta.agents.length > 0) {
    metadata.agents = params.meta.agents;
  }
  if (params.meta.file_path?.length) {
    metadata.file_path = params.meta.file_path;
  }
  if (Object.keys(metadata).length > 0) {
    answer.metadata = metadata;
  }

  const sourceMetadata = (params.meta.source_metadata || params.sourceMetadata)
    ? {
        ...params.sourceMetadata,
        ...params.meta.source_metadata
      }
    : undefined;
  if (sourceMetadata && Object.keys(sourceMetadata).length > 0) {
    answer.source_metadata = sourceMetadata;
  }

  if (params.meta.response_time != null) {
    answer.response_time = params.meta.response_time;
  }

  if (params.meta.updated_at) {
    answer.updated_at = params.meta.updated_at;
  }

  const imageVisualization = params.imageVisualization?.trim();
  if (imageVisualization) {
    answer.image_visualization = imageVisualization;
  }

  const payload: SaveAgentResponseRequest = {
    thread_id,
    tenant_id,
    user_id,
    supervisor: params.meta.supervisor,
    agents: params.meta.agents,
    question,
    answer
  };

  if (params.meta.response_time != null) {
    payload.response_time = params.meta.response_time;
  }

  applyCentralizedResponseFields(payload, params.meta);

  return payload;
}

@Injectable({
  providedIn: 'root'
})
export class SaveAgentResponseService {
  private readonly agentApiService = inject(AgentApiService);
  private readonly logger = inject(LoggerService);
  private readonly mappingService = inject(RegulationReferenceMappingService);
  private readonly tokenService = inject(TokenService);

  saveAgentResponse(
    payload: SaveAgentResponseRequest,
    agentId: string
  ): Observable<{ response_id?: string }> {
    const endpoint = this.agentApiService.getApiUrlFromConfig(SAVE_AGENT_RESPONSE_ENDPOINT, agentId);
    if (!endpoint) {
      this.logger.logInfo(
        `[SaveAgentResponse] No endpoint configured for agent: ${agentId}`,
        'saveAgentResponse'
      );
      return of({});
    }

    const customBaseUrl = this.agentApiService.getBaseUrlForEndpoint(SAVE_AGENT_RESPONSE_ENDPOINT, agentId);
    if (!customBaseUrl) {
      this.logger.logInfo(
        `[SaveAgentResponse] No API base URL for agent: ${agentId}`,
        'saveAgentResponse'
      );
      return of({});
    }

    return applyTenantInsIdsToAnswer(
      payload.answer,
      payload.tenant_id,
      this.mappingService,
      this.tokenService.getAccessToken()
    ).pipe(
      switchMap((remappedAnswer) => {
        const remappedPayload: SaveAgentResponseRequest =
          remappedAnswer === payload.answer
            ? payload
            : { ...payload, answer: remappedAnswer };
        return this.agentApiService.post<unknown>(endpoint, remappedPayload, undefined, customBaseUrl);
      }),
      map((response) => this.extractResponseId(response)),
      catchError((error) => {
        this.logger.logError(
          `[SaveAgentResponse] Save failed for agent ${agentId}: ${error?.message || error}`,
          'saveAgentResponse'
        );
        return of({});
      })
    );
  }

  private extractResponseId(response: unknown): { response_id?: string } {
    if (!response || typeof response !== 'object') {
      return {};
    }

    const root = response as Record<string, unknown>;
    const data = root['data'];

    const candidates = [
      root['response_id'],
      root['message_id'],
      data && typeof data === 'object' ? (data as Record<string, unknown>)['response_id'] : undefined,
      data && typeof data === 'object' ? (data as Record<string, unknown>)['message_id'] : undefined
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        return { response_id: candidate.trim() };
      }
    }

    return {};
  }
}
